import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSalePage } from '../../contexts/SalePageContext';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, Mail, Phone, MessageSquare, Clock, Loader, Trash2, CheckCheck,
  CreditCard, X,
} from 'lucide-react';
import SalePageNavbar from '../../components/SalePageNavbar';

const FONT = '"IBM Plex Sans Thai", system-ui, sans-serif';
const HEAD = "'Anuphan', sans-serif";
const INK = '#0E3A40';

interface ContactMessage {
  id: string;
  salePageId: string;
  salePageTitle: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  createdAt: any;
  status: 'new' | 'read' | 'replied';
}

function relTime(ts: any) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'เมื่อสักครู่';
  if (diff < 3600) return `${Math.floor(diff / 60)} นาทีที่แล้ว`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ชั่วโมงที่แล้ว`;
  if (diff < 172800) return 'เมื่อวาน';
  return new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}
function fullDate(ts: any) {
  if (!ts) return '-';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return new Intl.DateTimeFormat('th-TH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}
function statusMeta(status: string) {
  if (status === 'replied') return { label: 'ตอบกลับแล้ว', bg: 'rgba(0,180,161,0.12)', color: '#0E7C86' };
  if (status === 'read') return { label: 'อ่านแล้ว', bg: 'rgba(58,147,185,0.12)', color: '#276E8A' };
  return { label: 'ใหม่', bg: 'rgba(255,122,69,0.12)', color: '#B3552A' };
}

const SalePageMessages: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { isSubscriptionExpired } = useSalePage();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  useEffect(() => {
    const fetchMessages = async () => {
      if (!user?.uid) return;
      try {
        const q = query(
          collection(db, 'contactFormSubmissions'),
          where('ownerId', '==', user.uid),
          orderBy('createdAt', 'desc')
        );
        const snap = await getDocs(q);
        setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as ContactMessage[]);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, [user?.uid]);

  const isExpired = isSubscriptionExpired();

  const markAsRead = async (messageId: string) => {
    try {
      await updateDoc(doc(db, 'contactFormSubmissions', messageId), { status: 'read' });
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, status: 'read' as const } : m)));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!confirm('คุณต้องการลบข้อความนี้หรือไม่?')) return;
    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'contactFormSubmissions', messageId));
      setMessages((prev) => prev.filter((m) => m.id !== messageId));
      if (selectedMessage?.id === messageId) setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('เกิดข้อผิดพลาดในการลบข้อความ');
    } finally {
      setDeleting(false);
    }
  };

  const deleteAllMessages = async () => {
    if (isExpired) { alert('แพ็คเก็จหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานฟีเจอร์นี้'); return; }
    if (!confirm(`คุณต้องการลบข้อความทั้งหมด ${messages.length} ข้อความหรือไม่?`)) return;
    try {
      setDeleting(true);
      const batch = writeBatch(db);
      messages.forEach((m) => batch.delete(doc(db, 'contactFormSubmissions', m.id)));
      await batch.commit();
      setMessages([]);
      setSelectedMessage(null);
    } catch (error) {
      console.error('Error deleting all messages:', error);
      alert('เกิดข้อผิดพลาดในการลบข้อความ');
    } finally {
      setDeleting(false);
    }
  };

  const markAllAsRead = async () => {
    if (isExpired) { alert('แพ็คเก็จหมดอายุแล้ว กรุณาต่ออายุเพื่อใช้งานฟีเจอร์นี้'); return; }
    const unread = messages.filter((m) => m.status === 'new');
    if (unread.length === 0) { alert('ไม่มีข้อความใหม่ที่ต้องอ่าน'); return; }
    try {
      setMarkingAllRead(true);
      const batch = writeBatch(db);
      unread.forEach((m) => batch.update(doc(db, 'contactFormSubmissions', m.id), { status: 'read' }));
      await batch.commit();
      setMessages((prev) => prev.map((m) => (m.status === 'new' ? { ...m, status: 'read' as const } : m)));
    } catch (error) {
      console.error('Error marking all as read:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตสถานะ');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const openMessage = (m: ContactMessage) => {
    setSelectedMessage(m);
    if (m.status === 'new' && !isExpired) markAsRead(m.id);
  };

  const unreadCount = messages.filter((m) => m.status === 'new').length;
  const heroBtn: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 999,
    fontFamily: FONT, fontSize: 13, fontWeight: 500, border: 0, cursor: 'pointer',
    background: 'rgba(255,255,255,0.18)', color: '#fff',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F8F8', color: INK, fontFamily: FONT }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Anuphan:wght@400;500;600;700&family=IBM+Plex+Sans+Thai:wght@400;500;600&display=swap" rel="stylesheet" />
      </Helmet>

      <SalePageNavbar />

      {/* Expired banner */}
      {isExpired && (
        <div style={{ maxWidth: 1240, margin: '0 auto', padding: '14px clamp(16px,3vw,32px) 0' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 16, padding: '14px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Clock style={{ width: 20, height: 20, color: '#EF4444', flex: '0 0 auto' }} />
              <div style={{ fontSize: 13.5, color: '#B91C1C' }}>แพ็คเก็จหมดอายุแล้ว — ต่ออายุเพื่อดูและตอบข้อความจากลูกค้า</div>
            </div>
            <button type="button" onClick={() => navigate('/salepage/renew')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#EF4444', color: '#fff', border: 0, borderRadius: 999, padding: '9px 16px', fontFamily: HEAD, fontWeight: 600, fontSize: 13.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <CreditCard style={{ width: 15, height: 15 }} /> ต่ออายุเลย
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <div style={{ background: 'linear-gradient(115deg, #00B4A1 0%, #3A93B9 100%)', padding: 'clamp(20px,3vw,28px) clamp(16px,3vw,32px)', marginTop: isExpired ? 14 : 0 }}>
        <div style={{ maxWidth: 1240, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button type="button" onClick={() => navigate('/dashboard')} style={{ width: 38, height: 38, borderRadius: 999, background: 'rgba(255,255,255,0.16)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto', border: 0, cursor: 'pointer' }}>
              <ArrowLeft style={{ width: 18, height: 18, color: '#fff' }} />
            </button>
            <span style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
              <MessageSquare style={{ width: 18, height: 18, color: '#fff' }} />
            </span>
            <div>
              <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 20, color: '#fff' }}>ข้อความติดต่อ</div>
              <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.85)' }}>ข้อความจากลูกค้าที่ติดต่อผ่าน Sale Page</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {messages.length > 0 && (
              <>
                <button type="button" onClick={markAllAsRead} disabled={markingAllRead || unreadCount === 0 || isExpired} style={{ ...heroBtn, opacity: markingAllRead || unreadCount === 0 || isExpired ? 0.5 : 1 }}>
                  {markingAllRead ? <Loader style={{ width: 15, height: 15 }} className="animate-spin" /> : <CheckCheck style={{ width: 15, height: 15 }} />} อ่านทั้งหมด
                </button>
                <button type="button" onClick={deleteAllMessages} disabled={deleting || isExpired} style={{ ...heroBtn, background: 'rgba(239,68,68,0.85)', opacity: deleting || isExpired ? 0.5 : 1 }}>
                  {deleting ? <Loader style={{ width: 15, height: 15 }} className="animate-spin" /> : <Trash2 style={{ width: 15, height: 15 }} />} ลบทั้งหมด
                </button>
              </>
            )}
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)' }}>ทั้งหมด</div>
              <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 26, color: '#fff' }}>{messages.length}</div>
            </div>
          </div>
        </div>
      </div>

      <main style={{ maxWidth: 1000, margin: '0 auto', padding: 'clamp(22px,3vw,34px) clamp(16px,3vw,32px) clamp(48px,6vw,80px)' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '60px 0' }}>
            <Loader style={{ width: 40, height: 40, color: '#00B4A1' }} className="animate-spin" />
            <p style={{ color: '#5B7D80' }}>กำลังโหลดข้อความ...</p>
          </div>
        ) : messages.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 24, padding: 'clamp(50px,7vw,72px) clamp(24px,4vw,40px)', border: '1px solid rgba(14,58,64,0.06)', boxShadow: '0 30px 60px -48px rgba(14,58,64,0.35)', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
            <span style={{ width: 64, height: 64, borderRadius: 999, background: 'rgba(58,147,185,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageSquare style={{ width: 28, height: 28, color: '#3A93B9' }} />
            </span>
            <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 19 }}>ยังไม่มีข้อความ</div>
            <p style={{ fontSize: 14, color: '#5B7D80', margin: 0, maxWidth: '28em' }}>เมื่อมีคนติดต่อผ่าน Sale Page ข้อความจะแสดงที่นี่</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {messages.map((m) => {
              const unread = m.status === 'new';
              return (
                <div key={m.id} onClick={() => openMessage(m)} style={{ background: '#fff', borderRadius: 18, padding: 'clamp(16px,2vw,20px)', border: `1px solid ${unread ? 'rgba(255,122,69,0.35)' : 'rgba(14,58,64,0.06)'}`, display: 'flex', gap: 14, alignItems: 'flex-start', cursor: 'pointer' }}>
                  <span style={{ position: 'relative', width: 42, height: 42, borderRadius: 999, background: 'rgba(0,180,161,0.14)', color: '#0E7C86', fontFamily: HEAD, fontWeight: 600, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                    {(m.name || '?').charAt(0)}
                    {unread && <span style={{ position: 'absolute', top: -2, right: -2, width: 11, height: 11, borderRadius: 999, background: '#FF7A45', border: '2px solid #fff' }} />}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: HEAD, fontWeight: 600, fontSize: 15 }}>{m.name}</span>
                      <span style={{ fontSize: 12, color: '#8AA6A8', whiteSpace: 'nowrap' }}>{relTime(m.createdAt)}</span>
                    </div>
                    <p style={{ fontSize: 13.5, color: '#5B7D80', margin: '4px 0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.message}</p>
                    {m.salePageTitle && <span style={{ fontSize: 11.5, color: '#3A93B9', background: 'rgba(58,147,185,0.1)', padding: '3px 9px', borderRadius: 999 }}>{m.salePageTitle}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Detail modal */}
      {selectedMessage && (
        <div onClick={() => setSelectedMessage(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(14,58,64,0.4)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 24, padding: 'clamp(22px,3vw,30px)', width: '100%', maxWidth: 540, maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 40px 80px -30px rgba(14,58,64,0.5)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 18 }}>
              <div>
                <div style={{ fontFamily: HEAD, fontWeight: 700, fontSize: 20 }}>{selectedMessage.name}</div>
                {selectedMessage.salePageTitle && <div style={{ fontSize: 13, color: '#5B7D80', marginTop: 2 }}>จาก: {selectedMessage.salePageTitle}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, padding: '4px 10px', borderRadius: 999, ...(() => { const s = statusMeta(selectedMessage.status); return { background: s.bg, color: s.color }; })() }}>{statusMeta(selectedMessage.status).label}</span>
                <button type="button" onClick={() => setSelectedMessage(null)} style={{ background: 'transparent', border: 0, cursor: 'pointer', color: '#5B7D80' }}><X style={{ width: 20, height: 20 }} /></button>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: '#F3F8F8', borderRadius: 12 }}>
                <Mail style={{ width: 18, height: 18, color: '#5B7D80', flex: '0 0 auto' }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: '#8AA6A8' }}>อีเมล</div>
                  <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedMessage.email || '-'}</div>
                </div>
              </div>
              {selectedMessage.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: '#F3F8F8', borderRadius: 12 }}>
                  <Phone style={{ width: 18, height: 18, color: '#5B7D80', flex: '0 0 auto' }} />
                  <div>
                    <div style={{ fontSize: 12, color: '#8AA6A8' }}>เบอร์โทรศัพท์</div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{selectedMessage.phone}</div>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: '#F3F8F8', borderRadius: 12 }}>
                <Clock style={{ width: 18, height: 18, color: '#5B7D80', flex: '0 0 auto' }} />
                <div>
                  <div style={{ fontSize: 12, color: '#8AA6A8' }}>เวลาที่ส่ง</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{fullDate(selectedMessage.createdAt)}</div>
                </div>
              </div>
            </div>

            <div style={{ borderTop: '1px solid #EEF4F4', paddingTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: HEAD, fontWeight: 600, fontSize: 15, marginBottom: 10 }}>
                <MessageSquare style={{ width: 17, height: 17, color: '#00B4A1' }} /> ข้อความ
              </div>
              <div style={{ background: 'rgba(0,180,161,0.06)', border: '1px solid rgba(0,180,161,0.2)', borderRadius: 14, padding: 16, fontSize: 14, color: '#2A4E52', whiteSpace: 'pre-wrap' }}>{selectedMessage.message}</div>
            </div>

            <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a href={`mailto:${selectedMessage.email}`} style={{ flex: '1 1 160px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'linear-gradient(120deg, #00B4A1, #3A93B9)', color: '#fff', borderRadius: 999, padding: '13px', fontFamily: HEAD, fontWeight: 600, fontSize: 14.5 }}>
                <Mail style={{ width: 17, height: 17 }} /> ตอบกลับทางอีเมล
              </a>
              {selectedMessage.phone && (
                <a href={`tel:${selectedMessage.phone}`} style={{ flex: '1 1 120px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#fff', border: '1px solid rgba(14,58,64,0.14)', color: INK, borderRadius: 999, padding: '13px', fontFamily: HEAD, fontWeight: 600, fontSize: 14.5 }}>
                  <Phone style={{ width: 17, height: 17, color: '#00B4A1' }} /> โทรกลับ
                </a>
              )}
              <button type="button" onClick={() => deleteMessage(selectedMessage.id)} disabled={deleting} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#B91C1C', borderRadius: 999, padding: '13px 18px', fontFamily: HEAD, fontWeight: 600, fontSize: 14.5, cursor: 'pointer', opacity: deleting ? 0.5 : 1 }}>
                <Trash2 style={{ width: 17, height: 17 }} /> ลบ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalePageMessages;
